import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ArrowUpRight, RotateCcw, BookOpen, Building2, Briefcase,
  Calculator, Plane, FileSignature, ShieldCheck, TrendingUp, KeyRound, ClipboardList,
} from "lucide-react";
import { SERVICES_BY_SLUG } from "@/lib/data";

const ICONS = {
  BookOpen, Briefcase, Building2, FileSignature, Plane, ShieldCheck,
  Calculator, KeyRound, TrendingUp, ClipboardList, Sparkles,
};

// Two-question quiz → returns up to 3 recommended service slugs
const PRIORITIES = [
  { id: "organize", label: "Organize my finances / books", icon: "BookOpen", slugs: ["bookkeeping", "admin-services", "money-mindset"] },
  { id: "start", label: "Start a new business", icon: "Building2", slugs: ["business-formation", "consulting", "bookkeeping"] },
  { id: "grow", label: "Grow / scale my existing business", icon: "Briefcase", slugs: ["consulting", "admin-services", "business-credit"] },
  { id: "credit-personal", label: "Repair my personal credit", icon: "ShieldCheck", slugs: ["credit-repair", "money-mindset"] },
  { id: "credit-business", label: "Build business credit / get funded", icon: "TrendingUp", slugs: ["business-credit", "consulting"] },
  { id: "taxes", label: "File / save on taxes", icon: "Calculator", slugs: ["taxes", "bookkeeping"] },
  { id: "travel", label: "Plan a trip or group travel", icon: "Plane", slugs: ["travel"] },
  { id: "notary", label: "Notarize documents", icon: "FileSignature", slugs: ["notary"] },
  { id: "mindset", label: "Reset my money mindset", icon: "Sparkles", slugs: ["money-mindset", "consulting"] },
  { id: "home", label: "Buy a home (Journey to Keys™)", icon: "KeyRound", slugs: ["real-estate", "credit-repair", "money-mindset"] },
];

const PERSONA = [
  { id: "self", label: "Just for me / my family" },
  { id: "biz", label: "For my business / nonprofit" },
  { id: "both", label: "A bit of both" },
];

function recommendServices(priorityId, personaId) {
  const p = PRIORITIES.find((x) => x.id === priorityId);
  if (!p) return [];
  let slugs = [...p.slugs];
  if (personaId === "biz") {
    slugs = slugs.filter((s) => s !== "money-mindset");
    if (!slugs.includes("bookkeeping") && !slugs.includes("business-formation")) {
      slugs.push("bookkeeping");
    }
  }
  if (personaId === "self") {
    slugs = slugs.filter((s) => s !== "business-credit" && s !== "admin-services");
  }
  return slugs.slice(0, 3).map((slug) => SERVICES_BY_SLUG[slug]).filter(Boolean);
}

export default function HelpMeChoosePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [priority, setPriority] = useState(null);
  const [persona, setPersona] = useState(null);

  const recommendations = step === 2 ? recommendServices(priority, persona) : [];

  function reset() {
    setStep(0);
    setPriority(null);
    setPersona(null);
  }

  return (
    <div className="container-app py-16 md:py-24 relative" data-testid="quiz-page">
      <div className="blob bg-gold w-72 h-72 -top-20 -left-20 rounded-full" />
      <div className="blob bg-teal/40 w-96 h-96 bottom-0 -right-20 rounded-full" />

      <div className="relative max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="eyebrow"><Sparkles className="inline w-3 h-3 -mt-0.5 mr-1" /> Help Me Choose</div>
          <h1 className="mt-3 text-5xl md:text-6xl leading-tight">
            Two questions. <span className="italic text-gold">A tailored plan.</span>
          </h1>
          <p className="mt-4 text-navy/65 max-w-xl mx-auto">
            Answer two quick questions and we&apos;ll point you to the ConnectHer services that fit you best.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="quiz-progress">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i <= step ? "bg-gold w-12" : "bg-navy/10 w-8"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              data-testid="quiz-step-1"
            >
              <h2 className="text-2xl text-center mb-6">What&apos;s your top priority right now?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {PRIORITIES.map((p) => {
                  const Icon = ICONS[p.icon] || Sparkles;
                  return (
                    <button
                      key={p.id}
                      onClick={() => { setPriority(p.id); setStep(1); }}
                      data-testid={`priority-${p.id}`}
                      className="card p-5 text-left flex items-center gap-3 hover:border-gold hover:-translate-y-0.5 transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm text-navy">{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              data-testid="quiz-step-2"
            >
              <h2 className="text-2xl text-center mb-6">Is this for you personally or for a business?</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {PERSONA.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setPersona(p.id); setStep(2); }}
                    data-testid={`persona-${p.id}`}
                    className="card p-6 text-center hover:border-gold hover:-translate-y-0.5 transition"
                  >
                    <div className="text-navy font-serif text-xl">{p.label}</div>
                  </button>
                ))}
              </div>
              <div className="mt-6 text-center">
                <button onClick={() => setStep(0)} data-testid="quiz-back" className="text-sm text-navy/55 hover:text-navy underline">← Back</button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              data-testid="quiz-results"
            >
              <h2 className="text-2xl text-center mb-2">Here&apos;s where we&apos;d start:</h2>
              <p className="text-center text-navy/60 text-sm mb-8">
                {recommendations.length === 1
                  ? "One perfect match. Tap it to learn more."
                  : `${recommendations.length} services that fit you best — in order of priority.`}
              </p>
              <div className="space-y-4">
                {recommendations.map((s, i) => {
                  const Icon = ICONS[s.icon] || Sparkles;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`recommendation-${s.slug}`}
                      className="card p-6 flex items-center gap-5 hover:border-gold hover:-translate-y-1 transition group"
                    >
                      <div className="text-3xl font-serif text-gold w-10 flex-shrink-0">0{i + 1}</div>
                      <div className="w-12 h-12 rounded-2xl bg-gold/15 text-gold flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl text-navy">{s.name}{s.comingSoon && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold align-middle">Soon</span>}</h3>
                        <p className="text-sm text-navy/65 mt-1">{s.short}</p>
                        <div className="text-[10px] uppercase tracking-widest text-gold mt-2">{s.price_label}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-navy/40 group-hover:text-gold transition" />
                    </Link>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => navigate("/book")}
                  data-testid="quiz-book-cta"
                  className="btn-primary"
                >
                  Book Discovery Call <ArrowUpRight className="w-4 h-4" />
                </button>
                <button onClick={() => navigate("/intake")} data-testid="quiz-intake-cta" className="btn-secondary">
                  Start Intake
                </button>
                <button onClick={reset} data-testid="quiz-reset" className="inline-flex items-center gap-2 text-sm text-navy/55 hover:text-navy">
                  <RotateCcw className="w-4 h-4" /> Start over
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
